import { React, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./study-room-style.css";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import UserService from "../../../service/user-service";
import ProductService from "../../../service/product-service";
import AuthService from "../../../service/auth-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";

const ITEMS_PER_PAGE = 5;

//商品類型 → 卡片縮圖
const getBookImage = (productType) => {
  if (productType === "loveMatch") return "../images/bg-love-match.png";
  if (productType === "flowBook") return "../images/bg-flow-book.png";
  //親子合盤暫用真愛合盤書封，之後補專屬圖再換
  if (productType === "familyMatch") return "../images/bg-family-match.png";
  if (productType === "qimen") return "../images/bg-qimen-dunjia.png";
  return "../images/bg-personal-question.png"; //chart 及其他
};

//親子合盤的出生時辰：依序「父母、孩子」串成一行（用 " , " 分隔，跟同卡的姓名/性別/出生日期同風格）
//  people[0] = 父母、people[1] = 孩子
const buildFamilyBirthTime = (people) =>
  people
    .map((p) => p?.birthTime || "")
    .filter(Boolean)
    .join(" , ");

//Study doc → 卡片顯示用的形狀
const mapStudyToBook = (s) => {
  const people = Array.isArray(s.people) ? s.people : [];
  const between =
    s.productType === "loveMatch"
      ? s.extra?.betweenStatus === "1"
        ? "交往中"
        : "尚未交往"
      : "";

  //出生時辰：真愛合盤不顯示（雙方都用折衷時間）；親子合盤雙方都要顯示；其他類型只顯示 people[0]
  let birthTime = "";
  if (s.productType === "loveMatch") birthTime = "";
  else if (s.productType === "familyMatch")
    birthTime = buildFamilyBirthTime(people);
  else birthTime = people[0]?.birthTime || "";

  //reportStatus：舊資料沒這欄位（model default "ready"），這裡再 fallback 一次保險
  const reportStatus = s.reportStatus || "ready";

  //奇門遁甲卡片只顯示「姓名 / 盤別 / 事件」三個 label，給前端 JSX 用
  let customLabels = null;
  if (s.productType === "qimen") {
    customLabels = [
      { label: "姓名", value: people[0]?.name || "" },
      { label: "盤別", value: s.extra?.plateLabel || "" },
      { label: "事件", value: s.extra?.eventLabel || "" },
    ];
  }

  return {
    itemID: s.itemID, //給 React key + 輪詢比對用
    productType: s.productType,
    productID: s.productID,
    orderID: s.orderID || "",
    title: s.title,
    image: getBookImage(s.productType),
    name: people.map((p) => p.name).join(", "),
    gender: people.map((p) => p.gender).join(", "),
    birthday: people.map((p) => p.birth).join(" , "),
    birthTime,
    between,
    customLabels, //非 null 時 JSX 改用客製 label 列表
    reportStatus, //"generating" / "ready" / "failed"
    //老師有回覆且使用者尚未讀 → 顯示提醒「!」
    notification: s.consultUnread ? "1" : "0",
  };
};

const SrudyRoomCompontent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]); //累積已拉的書房項目 buffer
  const [totalRecords, setTotalRecords] = useState(0); //總筆數
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const registerFrom = location.pathname;

  //—— Auth gating ——
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  const fetchedRef = useRef(false);
  //用於管理區間塊（捲動回頂部用）
  const sectionRefs = useRef([]);

  //初次載入：先 auth gating，通過才拉資料
  useEffect(() => {
    if (fetchedRef.current) return;
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setIsLoading(false);
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      setIsLoading(false);
      setShowRegisterPrompt(true);
      return;
    }
    fetchedRef.current = true;
    fetchMore(0, ITEMS_PER_PAGE);
  }, []);

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        } else {
          fetchedRef.current = true;
          fetchMore(0, ITEMS_PER_PAGE);
        }
      }
    } catch (e) {
      console.error("[StudyRoom] firebase-login 失敗:", e);
    }
  };

  //如果現在的 records 裡有 reportStatus === "generating" 的書，每 5 秒靜默重新拉一次
  //  → AI 跑完後，後端 Study doc reportStatus 變 ready，前端卡片自動切「分析中...」→「查看內容」
  //  → 不顯示 loading dialog（避免畫面閃爍）
  //  → 全部 ready 就停輪詢
  useEffect(() => {
    const hasGenerating = records.some((b) => b.reportStatus === "generating");
    if (!hasGenerating) return;

    const POLL_INTERVAL_MS = 5000;
    let cancelled = false;

    const intervalId = setInterval(async () => {
      if (cancelled) return;
      try {
        //靜默 refetch 目前已載入的筆數（不重設 loading）
        const res = await UserService.getMyStudy(0, records.length);
        if (cancelled) return;
        if (res.data?.status) {
          const fresh = res.data.data.records || [];
          setRecords(fresh.map(mapStudyToBook));
          setTotalRecords(res.data.data.total);
        }
      } catch (e) {
        //靜默忽略網路錯誤，下一輪會再試
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [records]);

  //補拉書房項目（loadmore）— 顯示 loading dialog 至少 1 秒
  const fetchMore = async (skip, limit) => {
    setIsLoading(true);
    const MIN_LOADING_MS = 1000;
    const start = Date.now();
    try {
      const res = await UserService.getMyStudy(skip, limit);
      if (res.data?.status) {
        const { records: newRecords, total } = res.data.data;
        setRecords((prev) => [
          ...prev,
          ...(newRecords || []).map(mapStudyToBook),
        ]);
        setTotalRecords(total);
      }
    } catch (e) {
      console.log(e);
    } finally {
      const remaining = MIN_LOADING_MS - (Date.now() - start);
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
      setIsLoading(false);
    }
  };

  const scrollTo = (key) => {
    sectionRefs.current[key]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  //點到指定頁，缺資料就補拉，最後捲回該頁面區塊頂端
  const goToPage = async (targetPage) => {
    const needed = targetPage * ITEMS_PER_PAGE;
    if (records.length < needed && records.length < totalRecords) {
      await fetchMore(records.length, needed - records.length);
    }
    setCurrentPage(targetPage);
    scrollTo(isMobile ? 1 : 0);
  };

  //reportStatus 顯示：
  //  generating → 「分析中...」+ loading-dots 動畫
  //  failed    → 一樣顯示「分析中...」+ loading-dots（不可在使用者面前顯示「分析失敗」）
  //              使用者已付過錢，背景由後台監控 / 手動重跑處理
  //  ready     → 「查看內容」
  const getContentLabel = (book) => {
    if (book.reportStatus === "generating" || book.reportStatus === "failed") {
      return <span className="loading-dots">分析中</span>;
    }
    return "查看內容";
  };
  //目前是否允許點按（generating / failed 都不給點）
  const isBookReady = (book) =>
    !book.reportStatus || book.reportStatus === "ready";
  //統一的禁用視覺（沒有 SCSS 也能看出差異；之後要 polish 再到 scss 加 .disabled）
  const disabledStyle = { opacity: 0.5, cursor: "not-allowed" };

  //「專業諮詢」— 開啟該產品（訂單）的諮詢串
  //  orderID 放在網址 query，重新整理也不會遺失
  //  reportStatus 不為 ready 時直接 no-op（按鈕也應該禁用樣式）
  const handleConsult = (book) => {
    if (!isBookReady(book)) return;
    navigate(`/consult?order=${encodeURIComponent(book.orderID || "0")}`);
  };

  //「查看內容」— 依商品類型導到對應的報告頁
  //  reportStatus 不為 ready 時直接 no-op
  const handleViewContent = async (book) => {
    if (!isBookReady(book)) return;
    if (book.productType === "loveMatch") {
      try {
        const res = await ProductService.getLoveReport(book.productID);
        if (res.data?.status && res.data?.data) {
          navigate("/love-report", { state: { data: res.data.data } });
        } else {
          console.log("取得合盤報告失敗：", res.data?.message);
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }
    if (book.productType === "flowBook") {
      try {
        const res = await ProductService.getFlowYearReport(book.productID);
        if (res.data?.status && res.data?.data) {
          navigate("/flow-report", { state: { data: res.data.data } });
        } else {
          console.log("取得流年之書失敗：", res.data?.message);
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }
    if (book.productType === "familyMatch") {
      try {
        const res = await ProductService.getFamilyMatchReport(book.productID);
        if (res.data?.status && res.data?.data) {
          navigate("/family-report", { state: { data: res.data.data } });
        } else {
          console.log("取得親子合盤失敗：", res.data?.message);
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }
    if (book.productType === "chart") {
      navigate("/personal-chart");
      return;
    }
    if (book.productType === "qimen") {
      try {
        const res = await ProductService.getQimenReport(book.productID);
        if (res.data?.status && res.data?.data) {
          const r = res.data.data;
          navigate("/qimen-dunjia/result", {
            state: {
              event: r.event,
              plate: r.plate,
              date: r.date,
              results: r.results,
              dictionary: r.dictionary, //八門解析 / 步驟 / FAQ / 兇格（後端出）
            },
          });
        } else {
          console.log("取得奇門遁甲報告失敗：", res.data?.message);
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }
    console.log("尚未支援的商品類型：", book.productType);
  };

  //計算頁籤陣列
  const calcPages = (currentPage, total, windowSize = 3) => {
    if (!Number.isFinite(total) || total <= 0) return [];
    const safeTotal = Math.floor(total);
    const half = Math.floor(windowSize / 2);
    const p = Math.min(Math.max(1, Math.floor(currentPage)), safeTotal);
    let start = p - half;
    let end = p + half;
    if (start < 1) {
      end += 1 - start;
      start = 1;
    }
    if (end > safeTotal) {
      start -= end - safeTotal;
      end = safeTotal;
      if (start < 1) start = 1;
    }
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const total = Math.ceil(totalRecords / ITEMS_PER_PAGE); //總頁數
  const pagedBooks = records.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const pageArray = calcPages(currentPage, total);
  const displayBooks = pagedBooks;

  return (
    <div className="study-room-page">
      {/* API 載入中（至少顯示 1 秒）*/}
      {isLoading && <Loading />}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
          ref={(el) => {
            sectionRefs.current[0] = el;
          }}
        >
          <div className="study-main">
            <div className="title">
              <img src={"../images/ic-books.png"} alt=""></img>
              <span>我的書房</span>
            </div>

            {total > 0 && (
              <div className="books-list">
                {displayBooks.map((book, idx) => (
                  <div className="item-book" key={idx}>
                    <img src={book.image} alt=""></img>
                    <div className="item-info">
                      <div className="title">{book.title}</div>
                      <div className="line"></div>
                      <div className="item-labels">
                        {book.customLabels ? (
                          //奇門遁甲（或其他需要客製欄位的商品）→ 直接 render customLabels
                          book.customLabels.map((it, i) => (
                            <div className="label" key={i}>
                              <span className="label-title">{it.label}</span>
                              <span>{it.value}</span>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="label">
                              <span className="label-title">姓名</span>
                              <span>{book.name}</span>
                            </div>
                            <div className="label">
                              <span className="label-title">性別</span>
                              <span>{book.gender}</span>
                            </div>
                            <div className="label">
                              <span className="label-title">出生日期</span>
                              <span>{book.birthday}</span>
                            </div>
                            {book.birthTime && (
                              <div className="label">
                                <span className="label-title">
                                  出生時辰/真太陽時
                                </span>
                                <span>{book.birthTime}</span>
                              </div>
                            )}
                            {book.between && (
                              <div className="label">
                                <span className="label-title">目前狀態</span>
                                <span>{book.between}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="interaction">
                        <div className="expert">
                          <button
                            onClick={() => handleConsult(book)}
                            disabled={!isBookReady(book)}
                            style={!isBookReady(book) ? disabledStyle : undefined}
                          >
                            專業諮詢
                          </button>
                          {book.notification == "1" && (
                            <img src={"../images/ic-notification.png"} alt="" />
                          )}
                        </div>
                        <button
                          className="content"
                          onClick={() => handleViewContent(book)}
                          disabled={!isBookReady(book)}
                          style={!isBookReady(book) ? disabledStyle : undefined}
                        >
                          {getContentLabel(book)}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="books-selector">
                  <img
                    src={
                      currentPage - 1 == 0
                        ? "../images/ic-non-select-left.png"
                        : "../images/ic-select-left.png"
                    }
                    alt=""
                    onClick={() => {
                      if (currentPage - 1 == 0) return;
                      goToPage(currentPage - 1);
                    }}
                  ></img>
                  <div
                    className={`pages ${total == 1 ? "one-page" : total == 2 ? "two-page" : ""}`}
                  >
                    {pageArray.map((number) => (
                      <span
                        key={number}
                        className={number == currentPage ? "current-page" : ""}
                        onClick={() => {
                          if (number == currentPage) return;
                          goToPage(number);
                        }}
                      >
                        {number}
                      </span>
                    ))}
                  </div>
                  <img
                    src={
                      currentPage == total
                        ? "../images/ic-non-select-right.png"
                        : "../images/ic-select-right.png"
                    }
                    alt=""
                    onClick={() => {
                      if (currentPage == total) return;
                      goToPage(currentPage + 1);
                    }}
                  ></img>
                </div>
              </div>
            )}

            {total == 0 && !isLoading && (
              <div className="empty">
                <span>暫無收藏</span>
              </div>
            )}
          </div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {isMobile && (
        <div
          className="study-mobile-main"
          ref={(el) => {
            sectionRefs.current[1] = el;
          }}
        >
          <div className="sm-content">
            <div className="sm-title">
              <img src="../images/ic-books.png" alt="" />
              <span>我的書房</span>
            </div>

            {total > 0 && (
              <div className="sm-books-list">
                {displayBooks.map((book, idx) => (
                  <div className="sm-item-book" key={idx}>
                    <div className="sm-book-top">
                      <img src={book.image} alt="" />
                      <div className="sm-item-info">
                        <div className="sm-book-title">{book.title}</div>
                        <div className="sm-line"></div>
                        <div className="sm-item-labels">
                          {book.orderID && (
                            <div className="sm-label">
                              <span className="sm-label-title">訂單編號</span>
                              <span>{book.orderID}</span>
                            </div>
                          )}
                          {book.customLabels ? (
                            //奇門遁甲：客製顯示「姓名 / 盤別 / 事件」
                            book.customLabels.map((it, i) => (
                              <div className="sm-label" key={i}>
                                <span className="sm-label-title">
                                  {it.label}
                                </span>
                                <span>{it.value}</span>
                              </div>
                            ))
                          ) : (
                            <>
                              <div className="sm-label">
                                <span className="sm-label-title">姓名</span>
                                <span>{book.name}</span>
                              </div>
                              <div className="sm-label">
                                <span className="sm-label-title">姓別</span>
                                <span>{book.gender}</span>
                              </div>
                              <div className="sm-label">
                                <span className="sm-label-title">出生日期</span>
                                <span>{book.birthday}</span>
                              </div>
                              {book.birthTime && (
                                <div className="sm-label">
                                  <span className="sm-label-title">
                                    出生時辰/真太陽時
                                  </span>
                                  <span>{book.birthTime}</span>
                                </div>
                              )}
                              {book.between && (
                                <div className="sm-label">
                                  <span className="sm-label-title">目前狀態</span>
                                  <span>{book.between}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="sm-interaction">
                      <div className="sm-expert">
                        <button
                          className="sm-btn-consult"
                          onClick={() => handleConsult(book)}
                          disabled={!isBookReady(book)}
                          style={!isBookReady(book) ? disabledStyle : undefined}
                        >
                          專業諮詢
                        </button>
                        {book.notification === "1" && (
                          <img src="../images/ic-notification.png" alt="" />
                        )}
                      </div>
                      <button
                        className="sm-btn-content"
                        onClick={() => handleViewContent(book)}
                        disabled={!isBookReady(book)}
                        style={!isBookReady(book) ? disabledStyle : undefined}
                      >
                        {getContentLabel(book)}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {total > 0 && (
              <div
                className={`sm-books-selector ${total === 1 ? "one-page" : total === 2 ? "two-page" : ""}`}
              >
                <img
                  src={
                    currentPage - 1 === 0
                      ? "../images/ic-non-select-left.png"
                      : "../images/ic-select-left.png"
                  }
                  alt=""
                  onClick={() => {
                    if (currentPage - 1 !== 0) goToPage(currentPage - 1);
                  }}
                />
                <div className="sm-pages">
                  {pageArray.map((number) => (
                    <span
                      key={number}
                      className={number === currentPage ? "current-page" : ""}
                      onClick={() => {
                        if (number !== currentPage) goToPage(number);
                      }}
                    >
                      {number}
                    </span>
                  ))}
                </div>
                <img
                  src={
                    currentPage === total
                      ? "../images/ic-non-select-right.png"
                      : "../images/ic-select-right.png"
                  }
                  alt=""
                  onClick={() => {
                    if (currentPage !== total) goToPage(currentPage + 1);
                  }}
                />
              </div>
            )}

            {total === 0 && !isLoading && (
              <div className="sm-empty">
                <span>暫無收藏</span>
              </div>
            )}
          </div>

          <SiteFooter />
        </div>
      )}

      {/* 未登入訪客；關掉 → 留在當前頁 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="我的書房需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default SrudyRoomCompontent;
